import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import i18n from './i18n'
import { apiClient } from '@/lib/api/client'
import { ThemeProvider } from 'next-themes'
import * as Sentry from '@sentry/react'
import { getCachedDraftOverrides } from '@/lib/i18n-draft-cache'

// Suppress development warnings and browser extension noise
import './lib/utils/suppressWarnings'

// Optional Sentry init (only if DSN is set in Vite env)
if (import.meta.env.VITE_SENTRY_DSN) {
  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0),
      environment: import.meta.env.MODE,
    });
    if (import.meta.env.DEV) {
      console.info('Sentry im Frontend initialisiert');
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('Sentry konnte im Frontend nicht initialisiert werden', e);
    }
  }
}

async function bootstrapAndRender() {
  try {
    // Fetch platform language from a public i18n endpoint and enforce it
    const res = await apiClient.get('/i18n/character-attributes/translations');
    const lang = (res?.data?.language as string) || 'de';
    try {
      localStorage.setItem('i18nextLng', lang);
    } catch {}
    await i18n.changeLanguage(lang);

    // Load published UI text overrides and merge into i18next
    try {
      // CRITICAL: The '_t' timestamp is mandatory to bypass browser caching.
      // Without it, a hard refresh (CMD+Shift+R) might load an old version from cache,
      // causing recent text edits to "disappear" for the user.
      // DO NOT REMOVE THIS TIMESTAMP PARAMETER.
      const verRes = await apiClient.get('/i18n/overrides/version', {
        params: { _t: Date.now() }
      });
      const version = Number(verRes?.data?.version || 0);
      // Only fetch if version > 0
      if (version > 0) {
        const ovRes = await apiClient.get(`/i18n/overrides/${lang}`, {
          params: { v: version }
        });
        const resources = ovRes?.data?.resources as Record<string, Record<string, any>> | undefined;
        if (resources && typeof resources === 'object') {
          for (const ns of Object.keys(resources)) {
            const bundle = resources[ns] || {};
            // deep = true, overwrite = true
            i18n.addResourceBundle(lang, ns, bundle, true, true);
          }
        }
      }
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn('Konnte i18n Overrides nicht laden', e);
      }
      // Fail-safe: continue with defaults only
    }

    // Apply cached draft overrides from LocalStorage (for instant preview before publish)
    try {
      const cachedDrafts = getCachedDraftOverrides(lang);
      if (cachedDrafts && Object.keys(cachedDrafts).length > 0) {
        for (const ns of Object.keys(cachedDrafts)) {
          const bundle = cachedDrafts[ns] || {};
          i18n.addResourceBundle(lang, ns, bundle, true, true);
        }
      }
    } catch {
      // Fail silently - cached drafts are optional
    }
  } catch (e) {
    // Fallback to default if API not reachable
    try {
      localStorage.setItem('i18nextLng', 'de');
    } catch {}
    await i18n.changeLanguage('de');
  } finally {
    createRoot(document.getElementById("root")!).render(
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <App />
      </ThemeProvider>
    );
  }
}

void bootstrapAndRender();
